<template>
  <div ref="heroRef" :class="$style.wrap" data-hero-blocks-v="3.1.2">
    <div ref="swiperRef" :class="[$style.schedules, 'swiper']">
      <div class="swiper-wrapper">
        <div
          v-for="heroBlock in computedHeroBlocks"
          :key="heroBlock.sys.id"
          class="swiper-slide"
        >
          <HeroBlock
            :data="heroBlock"
            :with-nav="(computedHeroBlocks?.length || 0) > 1"
            @request-prev="onRequestPrev"
            @request-next="onRequestNext"
            @click="handleCTAEvent"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Swiper } from 'swiper'
import swiper from 'swiper'
import 'swiper/css'

import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

const heroRef = ref(null)
const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)
const { isIntersecting } = useIntersectionObserver(heroRef, {}, true)

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const computedHeroBlocks = computed(() =>
  props.data?.fields?.references?.filter?.(instanceOfHeroBlock),
)

const onRequestPrev = () => {
  swiperRef.value?.swiper.slidePrev()
}
const onRequestNext = () => {
  swiperRef.value?.swiper.slideNext()
}

const handleCTAEvent = () => {
  if (isIntersecting && swiperRef?.value?.swiper) {
    const { realIndex } = swiperRef.value.swiper
    const slide = computedHeroBlocks.value?.[realIndex]

    pushToGaDataLayer({
      event: 'ga4.trackEvent',
      eventName: `${useRoute().params?.slug?.[0] || 'homepage'}_banner_click`,
      eventParams: {
        content_position: realIndex + 1,
        content_name: slide?.fields?.entryTitle,
      },
    })
  }
}

const viewEvents = reactive<Record<number, boolean>>({})

function fireViewEvent() {
  if (isIntersecting && swiperRef?.value?.swiper) {
    const { realIndex = 0 } = swiperRef.value?.swiper || {}
    const slide = computedHeroBlocks.value?.[realIndex || 0]

    if (slide && !viewEvents[realIndex]) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'ga4.trackEvent',
        eventName: `${useRoute().params?.slug?.[0] || 'homepage'}_banner_view`,
        eventParams: {
          // item_name: slide?.fields?.productName, // Individual HeroBlock doesn't have item_name fields
          // set_name: slide?.fields?.setName, // Individual HeroBlock doesn't have set_name fields
          content_position: realIndex + 1,
          content_name: slide?.fields?.entryTitle,
        },
      })

      viewEvents[realIndex] = true
    }
  }
}

onMounted(() => {
  if (swiperRef.value) {
    new swiper(swiperRef.value, {})

    // on swipe init, fire view event
    fireViewEvent()

    // on swipe change, fire view event
    swiperRef.value.swiper.on('slideChange', () => {
      fireViewEvent()
    })
  }
})
</script>

<style lang="scss" module>
.wrap {
  background: var(--black);
}

.swiperSlide.swiperSlide {
  height: auto;
}

:global {
  .fade-left-enter-active {
    transition: transform cubic-bezier(0.16, 1, 0.29, 0.99) 0.667s;
  }

  .fade-left-enter, .fade-left-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
    transform: translateX(40px);
  }

  .fade-right-enter-active {
    transition: transform cubic-bezier(0.16, 1, 0.29, 0.99) 0.667s;
  }

  .fade-right-enter, .fade-right-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
    transform: translateX(-40px);
  }
}

:global {
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.4s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
  }
}
</style>
